import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/services/api'
import logger from '@/utils/logger'

export const useSettingsStore = defineStore('settings', () => {
    // State
    const settings = ref({
        // Dati Azienda
        company_name: 'Pasticceria Artigianale',
        company_description: 'Dal 1950, creiamo dolci momenti per le tue occasioni speciali',
        company_address: 'Via Roma 123, 12345 Città (PR)',
        company_phone: '+39 0123 456789',
        company_email: 'info@pasticceria.it',
        company_vat: 'IT12345678901',
        company_logo: null,

        // Orari
        opening_hours: {
            monday: { open: '08:00', close: '19:00', closed: false },
            tuesday: { open: '08:00', close: '19:00', closed: false },
            wednesday: { open: '08:00', close: '19:00', closed: false },
            thursday: { open: '08:00', close: '19:00', closed: false },
            friday: { open: '08:00', close: '19:00', closed: false },
            saturday: { open: '08:00', close: '19:00', closed: false },
            sunday: { open: '09:00', close: '18:00', closed: false }
        },

        // Home Page
        hero_title: 'Benvenuti nella nostra Pasticceria',
        hero_subtitle: 'Dolci tradizioni dal 1950',
        hero_image: '/images/hero-1.jpg',
        hero_cta_text: 'Scopri i nostri prodotti',
        hero_cta_link: '/store',

        featured_section_title: 'I nostri prodotti più amati',
        featured_section_subtitle: 'Scelti con cura per voi',

        about_section_title: 'La nostra storia',
        about_section_text: 'Da tre generazioni creiamo dolci momenti per le vostre occasioni speciali. La nostra passione per la pasticceria tradizionale si unisce all\'innovazione per offrirvi sempre il meglio.',
        about_image: '/images/hero-2.jpg',

        // Social Media
        social_facebook: '',
        social_instagram: '',
        social_twitter: '',
        social_youtube: '',

        // Header
        header_logo: null,
        header_tagline: 'Dolci momenti dal 1950',
        header_phone_display: true,
        header_email_display: true,
        header_social_display: true,

        // Navigation
        nav_items: [
            { name: 'Home', link: '/', active: true },
            { name: 'Prodotti', link: '/store', active: true },
            { name: 'Torte su Misura', link: '/cake-booking', active: true },
            { name: 'Chi Siamo', link: '/about', active: true },
            { name: 'Contatti', link: '/contact', active: true }
        ],

        // Footer
        footer_about_title: 'Chi Siamo',
        footer_about_text: 'Pasticceria artigianale dal 1950. Creiamo dolci momenti per le vostre occasioni speciali con ingredienti selezionati e ricette tradizionali.',
        footer_contact_title: 'Contatti',
        footer_hours_title: 'Orari di Apertura',
        footer_social_title: 'Seguici',
        footer_copyright: '© 2024 Pasticceria Artigianale. Tutti i diritti riservati.',
        footer_links: [
            { name: 'Privacy Policy', link: '/privacy', active: true },
            { name: 'Termini di Servizio', link: '/terms', active: true },
            { name: 'Cookie Policy', link: '/cookies', active: true }
        ],

        // SEO
        site_title: 'Pasticceria Artigianale - Dolci dal 1950',
        site_description: 'Pasticceria artigianale dal 1950. Torte, dolci e prodotti da forno freschi ogni giorno. Specializzati in torte personalizzate per ogni occasione.',
        site_keywords: 'pasticceria, torte, dolci, prodotti da forno, torte personalizzate',

        // Delivery
        delivery_enabled: true,
        delivery_zones: ['Centro', 'Periferia Nord', 'Periferia Sud'],
        delivery_fee: 5.00,
        delivery_free_above: 50.00,
        pickup_enabled: true,

        // Payment
        payment_cash: true,
        payment_card: true,
        payment_online: false
    })

    const isLoading = ref(false)
    const lastSaved = ref(null)

    // Actions
    const fetchSettings = async () => {
        logger.info('SETTINGS_STORE', '📋 Fetching settings from server')
        isLoading.value = true

        try {
            const response = await api.get('/settings')
            if (response.data && Object.keys(response.data).length > 0) {
                // Merge with defaults, keeping existing values
                settings.value = { ...settings.value, ...response.data }
                logger.info('SETTINGS_STORE', '✅ Settings loaded from server')
            } else {
                logger.info('SETTINGS_STORE', '📝 Using default settings (server empty)')
            }
            return true
        } catch (error) {
            logger.warn('SETTINGS_STORE', '⚠️ Failed to load settings from server, using defaults', {
                error: error.message
            })
            return false
        } finally {
            isLoading.value = false
        }
    }

    const saveSettings = async (settingsData) => {
        logger.info('SETTINGS_STORE', '💾 Saving settings to server')
        isLoading.value = true

        try {
            await api.put('/settings', settingsData || settings.value)

            if (settingsData) {
                settings.value = { ...settings.value, ...settingsData }
            }

            lastSaved.value = new Date().toISOString()
            logger.info('SETTINGS_STORE', '✅ Settings saved successfully')
            return { success: true }
        } catch (error) {
            logger.error('SETTINGS_STORE', '❌ Failed to save settings', {
                error: error.message,
                statusCode: error.response?.status
            })

            // Salva in locale se API fallisce
            localStorage.setItem('app_settings', JSON.stringify(settingsData || settings.value))
            lastSaved.value = new Date().toISOString()
            logger.info('SETTINGS_STORE', '💾 Settings saved locally as fallback')

            return { success: false, error: error.message }
        } finally {
            isLoading.value = false
        }
    }

    const updateSetting = async (key, value) => {
        settings.value[key] = value
        return await saveSettings({ [key]: value })
    }

    const resetToDefaults = () => {
        logger.info('SETTINGS_STORE', '🔄 Resetting settings to defaults')
        const defaultSettings = {
            company_name: 'Pasticceria Artigianale',
            company_description: 'Dal 1950, creiamo dolci momenti per le tue occasioni speciali',
            hero_title: 'Benvenuti nella nostra Pasticceria',
            hero_subtitle: 'Dolci tradizioni dal 1950',
            // ... altri defaults
        }
        settings.value = { ...settings.value, ...defaultSettings }
    }

    const uploadImage = async (file, type) => {
        logger.info('SETTINGS_STORE', `📸 Uploading ${type} image`, { fileName: file.name })

        const formData = new FormData()
        formData.append('image', file)
        formData.append('type', type)

        try {
            const response = await api.post('/settings/upload-image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })

            logger.info('SETTINGS_STORE', '✅ Image uploaded successfully')
            return response.data.url
        } catch (error) {
            logger.error('SETTINGS_STORE', '❌ Failed to upload image', {
                error: error.message,
                type
            })
            throw error
        }
    }

    // Load settings on store creation
    fetchSettings()

    return {
        // State
        settings,
        isLoading,
        lastSaved,

        // Actions
        fetchSettings,
        saveSettings,
        updateSetting,
        resetToDefaults,
        uploadImage
    }
}, {
    persist: {
        paths: ['settings', 'lastSaved']
    }
})
